
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { sendSwapUpdateNotification } from "../notifications";

// Delete (cancel) a swap request
export const deleteSwapRequest = async (
  swapRequestId: string
): Promise<boolean> => {
  try {
    // Get current user
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      toast.error("You must be logged in to cancel a swap request");
      return false;
    }

    const userId = sessionData.session.user.id;
    console.log("Deleting swap request:", { swapRequestId, userId });
    
    // Get swap request details to ensure it belongs to this user
    const { data: swapRequest, error: fetchError } = await supabase
      .from("swap_requests")
      .select(`
        id, 
        reagent_id, 
        requester_id,
        reagents!reagent_id(name, user_id)
      `)
      .eq("id", swapRequestId)
      .single();
    
    if (fetchError || !swapRequest) {
      console.error("Error fetching swap request:", fetchError);
      toast.error("Failed to verify swap request");
      return false;
    }
    
    // Verify ownership
    if (swapRequest.requester_id !== userId) {
      console.error("Ownership verification failed:", {
        requesterId: swapRequest.requester_id,
        currentUserId: userId
      });
      toast.error("You don't have permission to cancel this swap request");
      return false;
    }
    
    // Update the status to "cancelled" instead of deleting
    // This ensures we have a record and helps with synchronization issues
    const { error: updateError } = await supabase
      .from("swap_requests")
      .update({ 
        status: "cancelled",
        updated_at: new Date().toISOString()
      })
      .eq("id", swapRequestId);
      
    if (updateError) {
      console.error("Error cancelling swap request:", updateError);
      toast.error("Failed to cancel swap request");
      return false;
    }
    
    // Send a notification to the reagent owner
    const reagentOwnerId = swapRequest.reagents?.user_id;
    if (reagentOwnerId) {
      const actionButton = {
        actionButton: {
          text: "View Swap Request",
          url: `/swap-request/${swapRequestId}`,
          type: "secondary"
        }
      };

      const { error: messageError } = await supabase
        .from("messages")
        .insert({
          sender_id: userId,
          recipient_id: reagentOwnerId,
          reagent_id: swapRequest.reagent_id,
          content: `I've canceled my swap request for ${swapRequest.reagents.name}.`,
          read: false,
          payload: actionButton
        });
        
      if (messageError) {
        console.error("Error sending notification message:", messageError);
        toast.warning("Swap request canceled, but failed to send notification");
      }
      
      // Send email notification to the reagent owner
      sendSwapUpdateNotification(
        swapRequestId,
        swapRequest.reagents.name,
        reagentOwnerId,
        "canceled"
      ).catch(error => {
        console.error("Failed to send cancellation notification email:", error);
      });
    }
    
    console.log("Swap request cancelled successfully:", { swapRequestId });
    toast.success("Swap request canceled successfully");
    return true;
  } catch (error) {
    console.error("Exception in deleteSwapRequest:", error);
    toast.error("An unexpected error occurred");
    return false;
  }
};
